import type { ComponentPropsWithoutRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full font-medium whitespace-nowrap',
  {
    variants: {
      // Text on a tinted pill uses the `*-fg` token, not the fill: success at 500
      // measures only 3.85:1 against its own tint and fails AA.
      tone: {
        neutral: 'bg-surface-sunken text-text-secondary',
        primary: 'bg-primary-subtle text-text-link',
        success: 'bg-success-subtle text-success-fg',
        warning: 'bg-warning-subtle text-warning-fg',
        danger: 'bg-danger-subtle text-danger-fg',
        info: 'bg-info-subtle text-info-fg',
        outline: 'border border-border-default text-text-secondary',
      },
      size: {
        sm: 'h-5 px-2 text-2xs',
        md: 'h-6 px-2.5 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
);

export interface BadgeProps
  extends ComponentPropsWithoutRef<'span'>, VariantProps<typeof badgeVariants> {}

export function Badge({ className, tone, size, ...rest }: BadgeProps) {
  return <span className={cn(badgeVariants({ tone, size }), className)} {...rest} />;
}

/**
 * Unread counter, e.g. on the notification bell.
 *
 * Numbers are tabular so the badge does not jitter as the count changes, and the
 * digit cap keeps the pill from stretching the layout.
 */
export interface CountBadgeProps {
  count: number;
  max?: number;
  label?: string;
  className?: string;
}

export function CountBadge({ count, max = 99, label, className }: CountBadgeProps) {
  if (count <= 0) return null;
  const display = count > max ? `${max}+` : String(count);
  return (
    <span
      data-numeric
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5',
        'bg-danger text-2xs font-semibold text-text-on-danger',
        className,
      )}
      aria-label={label ?? `${count} unread`}
    >
      {display}
    </span>
  );
}
